import { useEffect, useState } from 'react'
import { useMutation } from 'urql'

import { prefectureOptionsMap } from '@/components/challenges/constants'
import { AiLoading } from '@/components/lottie/AiLoading'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { graphql } from '@/gql/generated'
import { GuardianMessageAiOptionToneType, JapanPrefecture } from '@/gql/generated/graphql'
import { cn } from '@/utils/tw-helpers'
import { Modal, ModalProps } from './Modal'

interface Props extends ModalProps {
  playerId: string
  targetableId: string
  guardianMessageLastAiOptionPrefecture?: JapanPrefecture | null
  defaultValues: {
    aiOptionToneType: GuardianMessageAiOptionToneType | null
    aiOptionPrefecture: JapanPrefecture | null
    aiOptionNoKanji: boolean
  } | null
  onOk: (
    props: {
      aiOptionToneType: GuardianMessageAiOptionToneType | null
      aiOptionPrefecture: JapanPrefecture | null
      aiOptionNoKanji: boolean
    } | null
  ) => void
  onRequestClose: () => void
  setMessage: (message: string) => void
}

export const AIDraftModal = ({
  isOpen,
  playerId,
  targetableId,
  guardianMessageLastAiOptionPrefecture,
  defaultValues,
  onOk,
  onRequestClose,
  setMessage,
  ...rest
}: Props): JSX.Element => {
  const [aiOptionToneType, setAiOptionToneType] =
    useState<GuardianMessageAiOptionToneType | null>(
      defaultValues?.aiOptionToneType ?? null
    )
  const [aiOptionPrefecture, setAiOptionPrefecture] =
    useState<JapanPrefecture | null>(defaultValues?.aiOptionPrefecture ?? null)
  const [aiOptionNoKanji, setAiOptionNoKanji] = useState(
    defaultValues?.aiOptionNoKanji ?? false
  )
  const [{ fetching: creatingAiDraft }, createGuardianMessageAiDraft] =
    useMutation(AIDraftModal_GuardianMessageAiDraftCreateMutation)

  const handleOk = async () => {
    if (!aiOptionToneType && !aiOptionPrefecture && !aiOptionNoKanji) {
      onOk(null)
      return
    }

    const { data } = await createGuardianMessageAiDraft({
      input: {
        playerId,
        targetableId,
        aiOptionToneType:
          aiOptionToneType ?? GuardianMessageAiOptionToneType.Casual,
        aiOptionPrefecture,
        aiOptionNoKanji,
      },
    })

    if (data?.guardianMessageAiDraftCreate?.errors) {
      // TODO: handle errors
      return
    }

    onOk({
      aiOptionToneType,
      aiOptionPrefecture,
      aiOptionNoKanji,
    })
    setMessage(data?.guardianMessageAiDraftCreate?.draft ?? '')
    onRequestClose()
  }

  useEffect(() => {
    if (!guardianMessageLastAiOptionPrefecture) return

    setAiOptionPrefecture(guardianMessageLastAiOptionPrefecture)
  }, [guardianMessageLastAiOptionPrefecture])

  return (
    <Modal
      isOpen={isOpen}
      customStyles={{
        overlay: {
          padding: '20px',
        },
        content: {
          padding: '24px',
          maxWidth: '318px',
          width: '100%',
          margin: 0,
          backgroundColor: '#F7F7F8',
        },
      }}
      {...rest}
      onRequestClose={onRequestClose}
      closeButtonClassName="top-3 right-3"
    >
      <h3 className="mb-4 text-[17px] font-bold leading-none text-primary-50">
        AIで下書きする
      </h3>

      <Label className="mb-3">メッセージタイプ</Label>

      <RadioGroup
        name="messageType"
        className="flex flex-col gap-3"
        value={aiOptionToneType ?? undefined}
        onValueChange={(value) => {
          setAiOptionToneType(value as GuardianMessageAiOptionToneType)
        }}
      >
        <Label className="flex h-12 items-center gap-x-2 rounded-md bg-white px-3">
          <RadioGroupItem value={GuardianMessageAiOptionToneType.Polite} />
          <span className="font-medium text-primary-text">丁寧語</span>
        </Label>
        <Label className="flex h-12 items-center gap-x-2 rounded-md bg-white px-3">
          <RadioGroupItem value={GuardianMessageAiOptionToneType.Casual} />
          <span className="font-medium text-primary-text">カジュアル</span>
        </Label>
        <Label className="flex h-12 items-center gap-x-2 rounded-md bg-white px-3">
          <RadioGroupItem value={GuardianMessageAiOptionToneType.Dialect} />
          <span className="font-medium text-primary-text">方言</span>
        </Label>
      </RadioGroup>

      <div className="mt-3">
        <Select
          disabled={
            aiOptionToneType !== GuardianMessageAiOptionToneType.Dialect
          }
          value={aiOptionPrefecture ?? undefined}
          onValueChange={(value) => {
            setAiOptionPrefecture(value as JapanPrefecture)
          }}
        >
          <SelectTrigger
            className={cn(
              'w-auto min-w-[145px] border-primary-50 bg-transparent !text-primary-50',
              'disabled:border-disable disabled:bg-disable disabled:!text-white disabled:opacity-100 [&:disabled_svg]:text-white [&_svg]:text-primary-50'
            )}
          >
            <SelectValue placeholder="選択してください" />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(prefectureOptionsMap).map(
              ([prefecture, prefectureName]) => (
                <SelectItem key={prefecture} value={prefecture}>
                  {prefectureName}
                </SelectItem>
              )
            )}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6">
        <h4 className="mb-3 text-xs font-bold text-secondary-text">
          オプション
        </h4>
        <Label className="flex items-center gap-x-2">
          <Checkbox
            name="noKanji"
            checked={aiOptionNoKanji}
            defaultChecked={defaultValues?.aiOptionNoKanji ?? false}
            onCheckedChange={(checked) => {
              setAiOptionNoKanji(checked === 'indeterminate' ? false : checked)
            }}
          />
          <span>漢字を使わない</span>
        </Label>
      </div>

      <Button
        disabled={
          !aiOptionToneType ||
          (aiOptionToneType === GuardianMessageAiOptionToneType.Dialect &&
            !aiOptionPrefecture)
        }
        className="mt-6 w-full"
        onClick={handleOk}
      >
        下書きを生成する
      </Button>

      <p className="mt-3 text-xs text-secondary-text">
        ※現在のメッセージが上書きされます。
      </p>

      {creatingAiDraft && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#F2F3F6]">
          <AiLoading autoplay={true} loop={true} className="w-[261px]" />
          <p className="text-[15px]">下書き生成中...</p>
        </div>
      )}
    </Modal>
  )
}

const AIDraftModal_GuardianMessageAiDraftCreateMutation = graphql(`
  mutation AIDraftModal_GuardianMessageAiDraftCreateMutation(
    $input: GuardianMessageAiDraftCreateInput!
  ) {
    guardianMessageAiDraftCreate(input: $input) {
      draft
      errors {
        ... on BaseError {
          __typename
          field
          message
        }
      }
    }
  }
`)
